import { message } from 'antd';
import { getExportDocTask } from '@/pages/documentCompile/api/data-project-doc';
import { downloadFile } from '@/utils/file';
import { getUrl } from '@/utils/request';
import { getStorage } from '@/utils/tools';

// 导出任务状态枚举
export enum ExportTaskStatus {
    PENDING = 1,     // 正在导出
    SUCCESS = 2,     // 导出成功
    FAILED = 3       // 导出失败
}

// 下载状态枚举
export enum DownloadStatus {
    NOT_STARTED = 0, // 未开始下载
    DOWNLOADING = 1, // 正在下载
    COMPLETED = 2,   // 下载完成
    FAILED = 3       // 下载失败
}

// 导出任务接口
export interface ExportTask {
    id: string;
    exportKey: string;
    fileName: string;
    status: ExportTaskStatus;
    downloadStatus: DownloadStatus;
    downloadUrl?: string;
    failReason?: string;
    exportFailInfos?: string[]; // 导出失败的文件列表
    createTime: number;
    retryCount: number;
    maxRetries: number;
    projectId: any;
}

// 导出任务管理器
class ExportTaskManager {
    private tasks: Map<string, ExportTask> = new Map();

    private pollingInterval = 2000; // 2秒轮询一次

    private pollingTimer: NodeJS.Timeout | null = null;

    private maxRetries = 3;

    private listeners: Set<(tasks: ExportTask[]) => void> = new Set();

    constructor() {
        this.startPolling();
    }

    // 添加任务状态监听器
    addListener(listener: (tasks: ExportTask[]) => void) {
        this.listeners.add(listener);
        // 立即通知当前状态
        listener(this.getAllTasks());
    }

    // 移除监听器
    removeListener(listener: (tasks: ExportTask[]) => void) {
        this.listeners.delete(listener);
    }

    // 通知所有监听器
    private notifyListeners() {
        const tasks = this.getAllTasks();
        this.listeners.forEach(listener => {
            try {
                listener(tasks);
            } catch (error) {
                console.error('Export task listener error:', error);
            }
        });
    }

    // 添加导出任务
    addTask(exportKey: string, fileName: string, projectId: any): string {
        const taskId = `${exportKey}_${Date.now()}`;
        const task: ExportTask = {
            id: taskId,
            exportKey,
            projectId,
            fileName,
            status: ExportTaskStatus.PENDING,
            downloadStatus: DownloadStatus.NOT_STARTED,
            createTime: Date.now(),
            retryCount: 0,
            maxRetries: this.maxRetries
        };

        this.tasks.set(taskId, task);
        this.notifyListeners();

        // 立即检查一次状态
        this.checkTaskStatus(taskId);

        return taskId;
    }

    // 获取所有任务
    getAllTasks(): ExportTask[] {
        return Array.from(this.tasks.values()).sort((a, b) => b.createTime - a.createTime);
    }

    // 获取进行中的任务数量
    getPendingTasksCount(): number {
        return Array.from(this.tasks.values()).filter(task => task.status === ExportTaskStatus.PENDING).length;
    }

    // 移除任务
    removeTask(taskId: string) {
        this.tasks.delete(taskId);
        this.notifyListeners();
    }

    // 清除所有已完成的任务
    clearCompletedTasks() {
        const pendingTasks = new Map();
        this.tasks.forEach((task, id) => {
            if (task.status === ExportTaskStatus.PENDING) {
                pendingTasks.set(id, task);
            }
        });
        this.tasks = pendingTasks;
        this.notifyListeners();
    }

    // 检查单个任务状态
    private async checkTaskStatus(taskId: string) {
        const task = this.tasks.get(taskId);
        if (!task || task.status !== ExportTaskStatus.PENDING) {
            return;
        }

        try {
            const response = await getExportDocTask({ exportKey: task.exportKey, projectId: task.projectId });
            if (response) {
                const taskInfo = response;
                if (taskInfo.status === ExportTaskStatus.SUCCESS) {
                    // 导出成功，但还未下载
                    task.status = ExportTaskStatus.SUCCESS;
                    task.downloadUrl = taskInfo.downloadUrl;
                    task.exportFailInfos = taskInfo.exportFailInfos || [];
                    task.downloadStatus = DownloadStatus.DOWNLOADING;
                    this.tasks.set(taskId, task);
                    this.notifyListeners();

                    // 自动下载文件
                    await this.downloadTaskFile(task);

                } else if (taskInfo.status === ExportTaskStatus.FAILED) {
                    // 导出失败
                    task.status = ExportTaskStatus.FAILED;
                    task.failReason = taskInfo.failReason || '导出失败';
                    task.exportFailInfos = taskInfo.exportFailInfos || [];
                    this.tasks.set(taskId, task);
                    this.notifyListeners();

                    message.error(`文件 "${task.fileName}" 导出失败: ${task.failReason}`);
                }
                // 如果还是 PENDING 状态，继续等待下次轮询
            }
        } catch (error) {
            console.error(`检查任务状态失败 (${taskId}):`, error);

            // 注释掉重试机制，避免额外问题
            // // 增加重试次数
            // task.retryCount++;

            // if (task.retryCount >= task.maxRetries) {
            //     // 超过最大重试次数，标记为失败
            //     task.status = ExportTaskStatus.FAILED;
            //     task.failReason = '检查导出状态失败，请重试';
            //     this.tasks.set(taskId, task);
            //     this.notifyListeners();

            //     message.error(`文件 "${task.fileName}" 导出状态检查失败`);
            // }
        }
    }

    // 下载任务文件
    private async downloadTaskFile(task: ExportTask) {
        if (!task.downloadUrl) {
            return;
        }

        try {
            let finalDownloadUrl = task.downloadUrl;

            // 如果是相对地址，拼接完整地址
            if (!task.downloadUrl.startsWith('http')) {
                finalDownloadUrl = `/cmp-doc/data-project-doc/${task.downloadUrl}`;
            }

            // 如果是相对地址，使用之前的下载逻辑
            if (!task.downloadUrl.startsWith('http')) {
                await downloadFile({
                    url: getUrl(finalDownloadUrl),
                    method: 'GET',
                    headers: {
                        'access-token': getStorage('token')
                    }
                });
            } else {
                // 如果是绝对地址，直接下载
                const link = document.createElement('a');
                link.href = finalDownloadUrl;
                link.download = task.fileName;
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
            }

            message.success(`文件 "${task.fileName}" 下载成功`);

            // 标记下载完成
            task.downloadStatus = DownloadStatus.COMPLETED;
            this.tasks.set(task.id, task);
            this.notifyListeners();

        } catch (error) {
            console.error('下载文件失败:', error);
            message.error(`文件 "${task.fileName}" 下载失败`);

            // 标记下载失败
            task.downloadStatus = DownloadStatus.FAILED;
            this.tasks.set(task.id, task);
            this.notifyListeners();
        }
    }

    // 手动重试下载
    async retryDownload(taskId: string) {
        // 注释掉重试机制，避免额外问题
        // const task = this.tasks.get(taskId);
        // if (!task || task.status !== ExportTaskStatus.SUCCESS) {
        //     return;
        // }

        // await this.downloadTaskFile(task);
        console.log('重试下载功能已禁用');
    }

    // 开始轮询
    private startPolling() {
        if (this.pollingTimer) {
            return;
        }

        this.pollingTimer = setInterval(() => {
            const pendingTasks = Array.from(this.tasks.values()).filter(
                task => task.status === ExportTaskStatus.PENDING
            );

            if (pendingTasks.length === 0) {
                return;
            }

            // 并发检查所有待处理任务的状态
            pendingTasks.forEach(task => {
                this.checkTaskStatus(task.id);
            });

        }, this.pollingInterval);
    }

    // 停止轮询
    stopPolling() {
        if (this.pollingTimer) {
            clearInterval(this.pollingTimer);
            this.pollingTimer = null;
        }
    }

    // 销毁管理器
    destroy() {
        this.stopPolling();
        this.tasks.clear();
        this.listeners.clear();
    }
}

// 创建全局单例
export const exportTaskManager = new ExportTaskManager();

// 页面卸载时清理
if (typeof window !== 'undefined') {
    window.addEventListener('beforeunload', () => {
        exportTaskManager.destroy();
    });
}
